import { PALETTE } from '@overlayqa/shared';
import { useState, createContext, useContext, useCallback } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { GridViewIcon, CancelSquareIcon } from '@hugeicons/core-free-icons';

interface GridSettings {
  visible: boolean;
  columns: number;
  gutterPx: number;
  marginPx: number;
  opacity: number;
  color: string;
  scope: 'content' | 'viewport';
  popoverOpen: boolean;
}

const DEFAULTS: GridSettings = {
  visible: false,
  columns: 12,
  gutterPx: 16,
  marginPx: 24,
  opacity: 0.3,
  color: '#FF00FF',
  scope: 'content',
  popoverOpen: false,
};

const COLOR_PRESETS = [
  { label: 'Pink', value: '#FF00FF' },
  { label: 'Blue', value: PALETTE.brand.primary },
  { label: 'Red', value: '#EF4444' },
  { label: 'Green', value: '#22C55E' },
];

interface GridContextValue {
  settings: GridSettings;
  update: (patch: Partial<GridSettings>) => void;
  reset: () => void;
}

const GridContext = createContext<GridContextValue | null>(null);

function useGrid(): GridContextValue {
  const ctx = useContext(GridContext);
  if (!ctx) throw new Error('useGrid must be used inside DevGridProvider');
  return ctx;
}

export function DevGridProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<GridSettings>(DEFAULTS);

  const update = useCallback((patch: Partial<GridSettings>) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  }, []);

  const reset = useCallback(() => setSettings(DEFAULTS), []);

  return (
    <GridContext.Provider value={{ settings, update, reset }}>
      {children}
    </GridContext.Provider>
  );
}

export function DevGridToggle() {
  const { settings, update } = useGrid();

  if (!import.meta.env.DEV) return null;

  return (
    <div className="relative">
      <button
        onClick={() => update({ popoverOpen: !settings.popoverOpen })}
        className={`p-2 rounded-md transition-colors ${
          settings.visible
            ? 'text-gray-700 bg-gray-200'
            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
        }`}
        title="Grid overlay"
        aria-label="Toggle grid overlay"
      >
        <HugeiconsIcon icon={GridViewIcon} size={20} strokeWidth={2} color="currentColor" className="w-5 h-5" aria-hidden="true" />
      </button>

      {settings.popoverOpen && <GridPopover />}
    </div>
  );
}

function GridPopover() {
  const { settings, update } = useGrid();

  return (
    <div
      className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl border border-gray-200 shadow-lg p-4 space-y-3"
      style={{ zIndex: 10000 }}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-900">Grid Overlay</span>
        <button
          onClick={() => update({ popoverOpen: false })}
          className="text-gray-400 hover:text-gray-600 p-1"
          aria-label="Close grid settings"
        >
          <HugeiconsIcon icon={CancelSquareIcon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>

      <NumberField label="Columns" value={settings.columns} min={1} max={24}
        onChange={(v) => update({ columns: v })} />
      <NumberField label="Gutter" value={settings.gutterPx} min={0} max={64} suffix="px"
        onChange={(v) => update({ gutterPx: v })} />
      <NumberField label="Margin" value={settings.marginPx} min={0} max={128} suffix="px"
        onChange={(v) => update({ marginPx: v })} />

      <div>
        <label className="text-xs font-medium text-gray-600 block mb-1">
          Opacity ({Math.round(settings.opacity * 100)}%)
        </label>
        <input
          type="range" min={0} max={100} value={Math.round(settings.opacity * 100)}
          onChange={(e) => update({ opacity: Number(e.target.value) / 100 })}
          className="w-full h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer accent-primary-500"
        />
      </div>

      <div>
        <label className="text-xs font-medium text-gray-600 block mb-1">Color</label>
        <select
          value={settings.color}
          onChange={(e) => update({ color: e.target.value })}
          className="w-full text-xs border border-gray-200 rounded-lg px-2 py-2 bg-white"
        >
          {COLOR_PRESETS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      <fieldset>
        <legend className="text-xs font-medium text-gray-600 mb-1">Scope</legend>
        <div className="space-y-1">
          {(['content', 'viewport'] as const).map((s) => (
            <label key={s} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
              <input
                type="radio" name="gridScope" value={s}
                checked={settings.scope === s}
                onChange={() => update({ scope: s })}
                className="accent-primary-500"
              />
              {s === 'content' ? 'Content area' : 'Full viewport'}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="flex gap-2 pt-1 border-t border-gray-100">
        <button
          onClick={() => update({ ...DEFAULTS, visible: false })}
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          <span className="text-cap-trim block">Reset</span>
        </button>
        <button
          onClick={() => update({ visible: !settings.visible })}
          className="ml-auto text-xs font-medium text-primary-600 hover:text-primary-700"
        >
          <span className="text-cap-trim block">{settings.visible ? 'Hide grid' : 'Show grid'}</span>
        </button>
      </div>
    </div>
  );
}

function NumberField({
  label, value, min, max, suffix, onChange,
}: {
  label: string; value: number; min: number; max: number;
  suffix?: string; onChange: (v: number) => void;
}) {
  const clamp = (n: number) => Math.max(min, Math.min(max, n));
  return (
    <div>
      <label className="text-xs font-medium text-gray-600 block mb-1">{label}</label>
      <div className="flex items-center gap-1">
        <button onClick={() => onChange(clamp(value - 1))}
          className="w-6 h-6 flex items-center justify-center rounded border border-gray-200 text-gray-500 hover:bg-gray-50 text-xs">
          -
        </button>
        <input
          type="number" min={min} max={max} value={value}
          onChange={(e) => onChange(clamp(Number(e.target.value) || min))}
          className="w-14 text-center text-xs border border-gray-200 rounded px-1 py-1"
        />
        <button onClick={() => onChange(clamp(value + 1))}
          className="w-6 h-6 flex items-center justify-center rounded border border-gray-200 text-gray-500 hover:bg-gray-50 text-xs">
          +
        </button>
        {suffix && <span className="text-xs text-gray-400">{suffix}</span>}
      </div>
    </div>
  );
}

export function DevGridOverlay() {
  const { settings } = useGrid();

  if (!import.meta.env.DEV) return null;
  if (!settings.visible) return null;

  const isContent = settings.scope === 'content';

  // Content scope: absolute within <main> which already accounts for sidebar width.
  // Viewport scope: fixed covering entire window.
  const style: React.CSSProperties = isContent
    ? {
        position: 'absolute',
        top: '3.5rem',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        pointerEvents: 'none',
        padding: `0 ${settings.marginPx}px`,
      }
    : {
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        pointerEvents: 'none',
        padding: `0 ${settings.marginPx}px`,
      };

  return (
    <div style={style}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${settings.columns}, 1fr)`,
          gap: `${settings.gutterPx}px`,
          height: '100%',
        }}
      >
        {Array.from({ length: settings.columns }, (_, i) => (
          <div
            key={i}
            style={{
              backgroundColor: settings.color,
              opacity: settings.opacity,
              height: '100%',
            }}
          />
        ))}
      </div>
    </div>
  );
}
